import { useCurrentUser, useUserInvitations } from '@billx/api';
import { getLabel } from '@billx/utils';
import {
  Badge,
  Box,
  Divider,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { BellIcon, CheckIcon, XIcon } from 'lucide-react';
import React, { useState } from 'react';

import { CustomMenu, UtilityIconButton } from '@/components/common';

export const NotificationsMenu = () => {
  const { currentUser } = useCurrentUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { invitations, acceptInvitation, declineInvitation } =
    useUserInvitations({
      status: ['pending'],
    });

  const pendingInvitations = invitations
    .filter((inv) => inv.status === 'pending')
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccept = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await acceptInvitation.mutateAsync(id);
  };

  const handleDecline = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentUser?.id) {
      await declineInvitation.mutateAsync({ id, userId: currentUser.id });
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <UtilityIconButton onClick={handleOpen}>
          <Badge badgeContent={pendingInvitations.length} color="error">
            <BellIcon size={20} />
          </Badge>
        </UtilityIconButton>
      </Tooltip>

      <CustomMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            width: 360,
            maxHeight: 480,
            overflow: 'auto',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box px={2} py={1.5}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
        </Box>
        <Divider />

        {pendingInvitations.length === 0 ? (
          <Box
            py={4}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            color="text.secondary"
          >
            <BellIcon size={32} style={{ opacity: 0.5, marginBottom: 8 }} />
            <Typography variant="body2">No new notifications</Typography>
          </Box>
        ) : (
          pendingInvitations.map((invitation) => (
            <MenuItem
              key={invitation.id}
              disableRipple
              sx={{
                py: 2,
                px: 2,
                cursor: 'default',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box display="flex" alignItems="flex-start" width="100%" gap={2}>
                <Box flex={1}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {invitation.businessName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Invited you to join as{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={500}
                      color="text.primary"
                    >
                      {getLabel('AccessRole', invitation.role)}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" mt={0.5}>
                    {new Date(invitation.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <UtilityIconButton
                    tooltip={{ title: 'Accept' }}
                    onClick={(e) => handleAccept(invitation.id, e)}
                    sx={{
                      color: 'success.main',
                      '&:hover': { bgcolor: 'success.lighter' },
                    }}
                  >
                    <CheckIcon size={18} />
                  </UtilityIconButton>
                  <UtilityIconButton
                    tooltip={{ title: 'Decline' }}
                    onClick={(e) => handleDecline(invitation.id, e)}
                    sx={{
                      color: 'error.main',
                      '&:hover': { bgcolor: 'error.lighter' },
                    }}
                  >
                    <XIcon size={18} />
                  </UtilityIconButton>
                </Stack>
              </Box>
            </MenuItem>
          ))
        )}
      </CustomMenu>
    </>
  );
};
